import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  Puzzle,
  BrainCircuit,
  Shield,
  Workflow,
  BarChart3,
  Key,
  Zap,
  Users,
  Code,
  Lock,
  Globe,
  DollarSign,
  Building2,
  Mail,
} from "lucide-react"

const features = [
  {
    icon: Puzzle,
    title: "Integrations",
    description: "Connect to 2000+ APIs and services",
    href: "/features/integrations",
    color: "text-blue-500",
  },
  {
    icon: BrainCircuit,
    title: "AI Gateway",
    description: "Unified LLM proxy and management",
    href: "/features/ai-gateway",
    color: "text-purple-500",
  },
  {
    icon: Shield,
    title: "Security",
    description: "Zero-trust with Cedar policies",
    href: "/features/security",
    color: "text-red-500",
  },
  {
    icon: Workflow,
    title: "Workflow Engine",
    description: "Visual automation builder",
    href: "/features/workflow-engine",
    color: "text-orange-500",
  },
  {
    icon: BarChart3,
    title: "Analytics",
    description: "Real-time metrics and insights",
    href: "/features/analytics",
    color: "text-cyan-500",
  },
  {
    icon: Key,
    title: "SSO & SAML",
    description: "Enterprise authentication",
    href: "/features/sso-saml",
    color: "text-green-500",
  },
]

const useCases = [
  { label: "API Management", href: "/use-cases/api-management" },
  { label: "AI Automation", href: "/use-cases/ai-automation" },
  { label: "Data Synchronization", href: "/use-cases/data-synchronization" },
  { label: "FinOps & Cost Optimization", href: "/use-cases/finops" },
  { label: "Enterprise Security", href: "/use-cases/enterprise-security" },
]

const concepts = [
  { label: "API Gateway", href: "/concepts/api-gateway" },
  { label: "API Management", href: "/concepts/api-management" },
  { label: "Data Integration", href: "/concepts/data-integration" },
  { label: "Webhooks", href: "/concepts/webhooks" },
  { label: "AI Agents", href: "/concepts/ai-agents" },
  { label: "Workflow Automation", href: "/concepts/workflow-automation" },
  { label: "Zero-Trust Security", href: "/concepts/zero-trust-security" },
  { label: "RBAC vs ABAC", href: "/concepts/rbac-abac" },
]

const comparisons = [
  { label: "vs Kong", href: "/comparisons/kong" },
  { label: "vs Apigee", href: "/comparisons/apigee" },
  { label: "vs AWS API Gateway", href: "/comparisons/aws-api-gateway" },
  { label: "vs Langfuse", href: "/comparisons/langfuse" },
  { label: "vs LangChain", href: "/comparisons/langchain" },
]

const companyLinks = [
  { icon: DollarSign, label: "Pricing", href: "/info/pricing" },
  { icon: Building2, label: "About Us", href: "/info/about" },
  { icon: Mail, label: "Contact", href: "/info/contact" },
]

const capabilities = [
  { icon: Zap, label: "Sub-10ms latency" },
  { icon: Users, label: "SSO & SAML" },
  { icon: Key, label: "API Key Management" },
  { icon: Code, label: "2000+ MCP Tools" },
  { icon: Lock, label: "End-to-end encryption" },
  { icon: Globe, label: "Global CDN" },
]

export function PlatformIndex() {
  return (
    <section id="platform" className="py-16 sm:py-24">
      <div className="container px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Explore Our Platform
          </h2>
          <p className="mt-3 text-muted-foreground">
            Everything you need to integrate, automate, and scale
          </p>
        </div>

        {/* Product Features */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Product Features</h3>
            <Link
              to="/features"
              className="text-sm text-primary hover:underline flex items-center"
            >
              View all features
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {features.map((feature) => (
              <Link
                key={feature.href}
                to={feature.href}
                className="group rounded-xl border bg-card p-4 transition-all hover:shadow-md hover:border-primary/50"
              >
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-muted ${feature.color}`}>
                  <feature.icon className="h-5 w-5" />
                </div>
                <h4 className="mt-3 font-medium text-sm group-hover:text-primary transition-colors">
                  {feature.title}
                </h4>
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                  {feature.description}
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-12 rounded-xl border bg-muted/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Use Cases & Solutions</h3>
            <Link
              to="/use-cases"
              className="text-sm text-primary hover:underline flex items-center"
            >
              Explore all solutions
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {useCases.map((useCase) => (
              <Link
                key={useCase.href}
                to={useCase.href}
                className="inline-flex items-center rounded-full border bg-background px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors"
              >
                {useCase.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Learn & Compare */}
        <div className="mb-12 grid gap-6 md:grid-cols-2">
          {/* Concepts */}
          <div className="rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Concepts</h3>
              <Link
                to="/concepts"
                className="text-sm text-primary hover:underline flex items-center"
              >
                Read all
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <ul className="grid grid-cols-2 gap-2">
              {concepts.map((concept) => (
                <li key={concept.href}>
                  <Link
                    to={concept.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {concept.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Comparisons */}
          <div className="rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Comparisons</h3>
              <Link
                to="/comparisons"
                className="text-sm text-primary hover:underline flex items-center"
              >
                View all
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <ul className="space-y-2">
              {comparisons.map((comparison) => (
                <li key={comparison.href}>
                  <Link
                    to={comparison.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {comparison.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Capabilities Bar */}
        <div className="mb-12 rounded-xl bg-muted/50 p-6">
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
            {capabilities.map((capability) => (
              <div key={capability.label} className="text-center">
                <capability.icon className="mx-auto h-6 w-6 text-primary" />
                <p className="mt-2 text-xs text-muted-foreground">{capability.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Company Links */}
        <div className="mb-12">
          <h3 className="text-lg font-semibold text-center mb-6">Company</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {companyLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-center gap-2 rounded-lg border bg-card px-5 py-3 hover:border-primary hover:text-primary transition-colors"
              >
                <link.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{link.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 p-8 sm:p-12 text-center">
          <h3 className="text-xl font-bold sm:text-2xl">Ready to Get Started?</h3>
          <p className="mt-2 text-muted-foreground">
            Join thousands of teams building with Matricks.ai
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" asChild>
              <Link to="/login">
                Start Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/info/contact">Schedule Demo</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}
